import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, X, Home as HomeIcon, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { api, formatPrice, formatApiErrorDetail } from "@/lib/api";

const emptyListing = {
  address: "", city: "", price: "", price_note: "", beds: "", baths: "", sqft: "",
  status: "Active", court_ordered: false, court_date: "",
  property_type: "featured", image_url: "", rew_url: "",
  description: "", featured: true,
};

const inputCls = "w-full bg-white border border-navy/20 px-3 py-2.5 font-body text-navy text-sm focus:outline-none focus:border-brass focus:ring-1 focus:ring-brass";

function ListingForm({ initial, onClose, onSaved }) {
  const [form, setForm] = useState({ ...emptyListing, ...initial });
  const [saving, setSaving] = useState(false);
  const editing = Boolean(initial?.id);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    const payload = {
      address: form.address, city: form.city,
      price: form.price ? parseInt(form.price, 10) : null,
      price_note: form.price_note || null,
      beds: form.beds !== "" ? parseInt(form.beds, 10) : null,
      baths: form.baths !== "" ? parseFloat(form.baths) : null,
      sqft: form.sqft ? parseInt(form.sqft, 10) : null,
      status: form.status,
      court_ordered: Boolean(form.court_ordered),
      court_date: form.court_date || null,
      property_type: form.property_type,
      image_url: form.image_url,
      rew_url: form.rew_url || null,
      description: form.description,
      featured: form.property_type === "featured",
    };
    try {
      if (editing) await api.put(`/listings/${initial.id}`, payload);
      else await api.post("/listings", payload);
      toast.success(editing ? "Listing updated." : "Listing created.");
      onSaved();
      onClose();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 bg-navy/60 flex items-start justify-center overflow-y-auto py-10 px-4" data-testid="listing-form-modal">
      <div className="bg-paper w-full max-w-2xl">
        <div className="flex items-center justify-between px-6 py-4 bg-navy text-white">
          <h3 className="font-heading text-xl">{editing ? "Edit Listing" : "New Listing"}</h3>
          <button onClick={onClose} data-testid="listing-form-close"><X size={22} /></button>
        </div>
        <form onSubmit={save} className="p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-4">
            <div><label className="overline block mb-1">Address</label><input data-testid="form-address" className={inputCls} value={form.address} onChange={(e) => set("address", e.target.value)} required /></div>
            <div><label className="overline block mb-1">City</label><input data-testid="form-city" className={inputCls} value={form.city} onChange={(e) => set("city", e.target.value)} required /></div>
          </div>
          <div className="grid sm:grid-cols-4 gap-4">
            <div><label className="overline block mb-1">Price ($)</label><input data-testid="form-price" type="number" className={inputCls} value={form.price} onChange={(e) => set("price", e.target.value)} placeholder="Leave blank if TBD" /></div>
            <div><label className="overline block mb-1">Beds</label><input data-testid="form-beds" type="number" className={inputCls} value={form.beds} onChange={(e) => set("beds", e.target.value)} /></div>
            <div><label className="overline block mb-1">Baths</label><input data-testid="form-baths" type="number" step="0.5" className={inputCls} value={form.baths} onChange={(e) => set("baths", e.target.value)} /></div>
            <div><label className="overline block mb-1">Sq Ft</label><input data-testid="form-sqft" type="number" className={inputCls} value={form.sqft || ""} onChange={(e) => set("sqft", e.target.value)} /></div>
          </div>
          <div>
            <label className="overline block mb-1">Price note (overrides price display)</label>
            <input data-testid="form-price-note" className={inputCls} value={form.price_note} onChange={(e) => set("price_note", e.target.value)} placeholder="e.g. Accepted Offer · Court Date Sept 10" />
          </div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-1">Status</label>
              <select data-testid="form-status" className={inputCls} value={form.status} onChange={(e) => set("status", e.target.value)}>
                {["Active", "Pending", "Sold"].map((s) => <option key={s}>{s}</option>)}
              </select>
            </div>
            <div>
              <label className="overline block mb-1">Category</label>
              <select data-testid="form-type" className={inputCls} value={form.property_type} onChange={(e) => set("property_type", e.target.value)}>
                <option value="featured">Featured (Mike's)</option>
                <option value="office">Office (RE/MAX)</option>
              </select>
            </div>
          </div>
          <div><label className="overline block mb-1">Image URL</label><input data-testid="form-image" className={inputCls} value={form.image_url} onChange={(e) => set("image_url", e.target.value)} placeholder="https://…" required /></div>
          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <label className="overline block mb-1">REW URL (opens on card click)</label>
              <input data-testid="form-rew" className={inputCls} value={form.rew_url} onChange={(e) => set("rew_url", e.target.value)} placeholder="https://www.rew.ca/properties/…" />
            </div>
            <div>
              <label className="overline block mb-1">Court date (if applicable)</label>
              <input data-testid="form-court-date" className={inputCls} value={form.court_date} onChange={(e) => set("court_date", e.target.value)} placeholder="e.g. Sept 10" />
            </div>
          </div>
          <label className="flex items-center gap-2 cursor-pointer">
            <input data-testid="form-court-ordered" type="checkbox" className="accent-[#A57F35] w-4 h-4" checked={Boolean(form.court_ordered)} onChange={(e) => set("court_ordered", e.target.checked)} />
            <span className="font-body text-sm text-navy">Court-Ordered sale (shows badge on card)</span>
          </label>
          <div><label className="overline block mb-1">Description</label><textarea data-testid="form-description" rows={3} className={inputCls} value={form.description} onChange={(e) => set("description", e.target.value)} /></div>
          <div className="flex justify-end gap-3 pt-2">
            <button type="button" onClick={onClose} className="px-5 py-2.5 border border-navy/20 text-navy text-sm hover:bg-navy/5">Cancel</button>
            <button data-testid="form-submit" type="submit" disabled={saving} className="px-6 py-2.5 bg-navy text-white text-sm hover:bg-brass transition-colors disabled:opacity-60">
              {saving ? "Saving…" : editing ? "Update" : "Create"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("listings");
  const [listings, setListings] = useState([]);
  const [leads, setLeads] = useState([]);
  const [unread, setUnread] = useState(0);
  const [modal, setModal] = useState(null); // null | {} | listing

  const load = useCallback(() => {
    api.get("/listings").then((r) => setListings(r.data)).catch(() => {});
    api.get("/leads").then((r) => setLeads(r.data)).catch(() => {});
    api.get("/leads/unread-count").then((r) => setUnread(r.data.unread || 0)).catch(() => {});
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    // When the user opens the leads tab, mark everything as read.
    if (tab === "leads" && unread > 0) {
      api.patch("/leads/mark-all-read").then(() => {
        setUnread(0);
        setLeads((prev) => prev.map((l) => ({ ...l, read: true })));
      }).catch(() => {});
    }
  }, [tab, unread]);

  const remove = async (id) => {
    if (!window.confirm("Delete this listing?")) return;
    try {
      await api.delete(`/listings/${id}`);
      toast.success("Listing deleted.");
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Delete failed.");
    }
  };

  const doLogout = () => { logout(); navigate("/admin/login"); };

  return (
    <div data-testid="admin-dashboard" className="min-h-screen bg-paper">
      <header className="bg-navy text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-20 flex items-center justify-between">
          <div>
            <p className="font-heading text-xl">Listings Manager</p>
            <p className="font-mono text-[0.6rem] uppercase tracking-[0.2em] text-white/50">{user?.email}</p>
          </div>
          <div className="flex items-center gap-3">
            <button onClick={() => navigate("/")} data-testid="admin-view-site" className="flex items-center gap-2 text-sm text-white/80 hover:text-brass"><HomeIcon size={16} /> View Site</button>
            <button onClick={doLogout} data-testid="admin-logout" className="flex items-center gap-2 text-sm bg-white/10 hover:bg-brass px-4 py-2 transition-colors"><LogOut size={16} /> Logout</button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <div className="flex items-center gap-6 border-b border-navy/10 mb-8">
          <button data-testid="tab-listings" onClick={() => setTab("listings")} className={`pb-3 font-mono text-xs uppercase tracking-[0.15em] border-b-2 -mb-px ${tab === "listings" ? "border-brass text-brass" : "border-transparent text-ink-soft"}`}>Listings ({listings.length})</button>
          <button data-testid="tab-leads" onClick={() => setTab("leads")} className={`pb-3 font-mono text-xs uppercase tracking-[0.15em] border-b-2 -mb-px flex items-center gap-2 ${tab === "leads" ? "border-brass text-brass" : "border-transparent text-ink-soft"}`}><Inbox size={14} /> Leads ({leads.length}){unread > 0 && (<span data-testid="leads-unread-badge" className="ml-1 bg-brass text-white font-mono text-[0.6rem] px-1.5 py-0.5 leading-none">{unread} NEW</span>)}</button>
        </div>

        {tab === "listings" && (
          <>
            <div className="flex justify-end mb-6">
              <button data-testid="add-listing-btn" onClick={() => setModal({})} className="flex items-center gap-2 bg-navy text-white px-5 py-3 text-sm hover:bg-brass transition-colors"><Plus size={17} /> Add Listing</button>
            </div>
            <div className="bg-white border border-navy/10 divide-y divide-navy/10">
              {listings.map((l) => (
                <div key={l.id} data-testid={`admin-listing-${l.id}`} className="flex items-center gap-4 p-4">
                  <img src={l.image_url} alt="" className="w-20 h-16 object-cover shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="font-heading text-navy truncate">{l.address}</p>
                    <p className="font-mono text-xs text-ink-soft">{l.city} · {formatPrice(l.price)} · <span className="text-brass">{l.status}</span> · {l.property_type}</p>
                  </div>
                  <button data-testid={`edit-listing-${l.id}`} onClick={() => setModal(l)} className="p-2 text-navy hover:text-brass"><Pencil size={18} /></button>
                  <button data-testid={`delete-listing-${l.id}`} onClick={() => remove(l.id)} className="p-2 text-navy hover:text-red-600"><Trash2 size={18} /></button>
                </div>
              ))}
              {listings.length === 0 && <p className="p-8 text-center font-body text-ink-soft">No listings yet.</p>}
            </div>
          </>
        )}

        {tab === "leads" && (
          <div className="bg-white border border-navy/10 divide-y divide-navy/10">
            {leads.map((ld) => (
              <div key={ld.id} data-testid={`admin-lead-${ld.id}`} className={`p-5 ${!ld.read ? "bg-brass/5 border-l-2 border-brass" : ""}`}>
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <p className="font-heading text-lg text-navy">{ld.name}</p>
                    {!ld.read && <span className="font-mono text-[0.55rem] uppercase tracking-[0.18em] bg-brass text-white px-1.5 py-0.5">New</span>}
                    {ld.source && <span className="font-mono text-[0.55rem] uppercase tracking-[0.18em] border border-navy/20 text-ink-soft px-1.5 py-0.5">{ld.source.replace(/_/g, " ")}</span>}
                  </div>
                  <p className="font-mono text-[0.65rem] text-ink-soft">{new Date(ld.created_at).toLocaleString("en-CA")}</p>
                </div>
                <p className="font-mono text-xs text-ink-soft mt-1">{ld.email || "no email"} · {ld.phone || "no phone"} {ld.interested_in_mortgage && <span className="text-brass">· wants DLC referral</span>}</p>
                {ld.message && <p className="font-body text-navy text-sm mt-3">{ld.message}</p>}
              </div>
            ))}
            {leads.length === 0 && <p className="p-8 text-center font-body text-ink-soft">No leads yet.</p>}
          </div>
        )}
      </div>

      {modal !== null && <ListingForm initial={modal} onClose={() => setModal(null)} onSaved={load} />}
    </div>
  );
}
